// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerMetadataApolloGradeRow,
  PlayerMetadataScoreRow,
  PlayerRow,
  PlayerSummaryRow,
} from "@suns/api/generated-client/apollo";
import { cn, Flex, Separator, Text } from "@suns/design-system";
import { PlayerHeadshot, TeamLogo } from "@/components";
import { CanDefendSection, scoutingLoader } from "./PlayerScouting";
import { PlayerDetails } from "./PlayerDetails";
import ApolloGradeChart from "./ApolloGradeChart";
import {
  ReportScoreDescriptions,
  SkillScoreSections,
} from "@/pages/reports/reports-const";
import { PlayerHealthAndPerformance } from "./PlayerHealthAndPerformance";
import { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import SunsLogo from "@/assets/favicon/favicon-32x32.png";
import { theme } from "@/shared/theme";
import {
  ApolloAnalyticsIcon,
  ApolloScoutIcon,
} from "@suns/design-system/icons";
import {
  formatAge,
  formatInches,
  formatWeight,
} from "@/shared/utils/helper-functions";
import dayjs from "dayjs";
import countries from "../country-code.json";
import PlayerProfileHeader from "./PlayerProfileHeader";
import ScoreBarChart from "./ScoreBarChart";
import { PlayerOverview } from "./PlayerOverview";
import PlayerAttributes from "./PlayerAttributes";
import { AiEditor } from "./AiEditor";
import { useAsync } from "@/shared/hooks";
import { PlayerAnalytics } from "./PlayerAnalytics";
import { TempNbaPlayerStats } from "./PlayerOverview/components";
import { PlayersStats } from "./PlayersStats";
import { LeagueId } from "@suns/api";

export function PlayerPrintPage({
  player,
  scores,
  direction = "down",
  className,
}: {
  player: PlayerRow;
  scores?: PlayerMetadataScoreRow[] | null;
  direction?: "down" | "right";
  className?: string;
}) {
  const { loading, response } = useAsync(scoutingLoader, player.id);

  const isPro = Boolean(player.nbaId);
  const hasScores = scores && scores.length > 0;

  if (loading) return <Text>Loading...</Text>;

  return (
    <Flex
      direction={direction}
      gap="md"
      className={cn("print:flex-col print:gap-0", className)}
    >
      <OverviewPage player={player} />
      {isPro && <ProAnalyticsPage player={player} />}
      <ScoutingAndAnalyticsPage1
        player={player}
        scores={scores}
        offenseSummary={response?.offenseSummary}
        defenseSummary={response?.defenseSummary}
        otherSummary={response?.otherSummary}
      />
      {(!isPro || hasScores) && (
        <ScoutingAndAnalyticsPage2
          player={player}
          scores={scores}
          otherSummary={response?.otherSummary}
        />
      )}
      {isPro && <StatsPage player={player} />}
      <HealthAndPerformancePage player={player} />
    </Flex>
  );
}

function Paper({
  player,
  children,
  qrCode = true,
}: {
  player: PlayerRow;
  children: React.ReactNode;
  qrCode?: boolean;
}) {
  return (
    <Flex
      direction="down"
      className="h-letter w-letter break-inside-avoid bg-white p-[0.4in]"
    >
      <Flex className="relative m-0 h-full overflow-hidden">
        {children}
        {qrCode && (
          <QRCode
            player={player}
            className="absolute bottom-0 right-0 h-[80px] w-[80px]"
          />
        )}
      </Flex>
    </Flex>
  );
}

function OverviewPage({ player }: { player: PlayerRow }) {
  const playerCountry = player.homeCountryCode
    ? getCountryName(player.homeCountryCode ?? "")
    : player.homeCountryCode;

  const playerNationality = player.nationalityCountryCode
    ? getCountryName(player.nationalityCountryCode ?? "")
    : player.nationalityCountryCode;

  const isNbaPlayer = player.currentTeams?.some(
    (team) => team.domesticLeagueId == LeagueId.NBA
  );

  return (
    <Paper player={player}>
      <Flex direction="down" gap="sm" className="w-full">
        <Flex className="w-full" direction="right" justify="end">
          <IconLegend />
        </Flex>
        <Flex direction="down" gap="none">
          <PlayerProfileHeader player={player} print />
          <Separator />
        </Flex>
        <Flex direction="right" gap="sm" className="mb-2 w-full">
          <PlayerDetails
            title="Player Info"
            items={[
              {
                label: "Birthdate",
                value: player.birthDate
                  ? `${dayjs(player.birthDate).format("MM/DD/YYYY")} (${formatAge(player.birthDate)})`
                  : "--",
              },
              { label: "High School", value: player.highschool },
              {
                label: "Birthplace",
                value: `${player.homeTown}, ${playerCountry}`,
              },
              { label: "Nationality", value: playerNationality },
            ]}
            print
          />
          <PlayerDetails
            title="Measurments"
            items={[
              {
                label: "Height",
                value: player.height ? formatInches(player?.height) : "--",
              },
              {
                label: "Weight",
                value: player.weight ? formatWeight(player.weight) : "--",
              },
              {
                label: "Wing",
                value: player.wing ? formatInches(player?.wing) : "--",
              },
              {
                label: "Reach",
                value: player.reach ? formatInches(player?.reach) : "--",
              },
            ]}
            print
          />
          <PlayerDetails
            title="Contact"
            items={[
              {
                label: "Agent",
                value: player.agent?.name ?? "--",
              },
              {
                label: "Agency",
                value: player.agent?.agency?.name ?? "--",
              },
            ]}
            print
          />
        </Flex>

        <ApolloGradeChart
          metadata={player.metadata[0]!}
          print
          displayNumbers={true}
          grouping={PlayerMetadataApolloGradeRow.grouping.HYBRID}
        />

        <PlayerOverview
          playerId={player.id}
          isNbaPlayer={isNbaPlayer}
          nbaId={player.nbaId ?? undefined}
          print
        />
      </Flex>
    </Paper>
  );
}

function SmallPlayerHeader({
  player,
  title,
}: {
  player: PlayerRow;
  title: string;
}) {
  return (
    <Flex
      direction="right"
      gap="lg"
      justify="between"
      align="center"
      className="w-full"
    >
      <Flex direction="right" align="center" gap="lg">
        <PlayerHeadshot
          src={player.image}
          nbaPlayerId={player.nbaId}
          leagueId={player.currentTeams?.[0]?.domesticLeagueId}
          size="md"
        />
        <Flex direction="down">
          <Text size="xl" heading>
            {player.firstName} {player.lastName}
          </Text>
          <Flex direction="right" align="center" gap="sm">
            {player.currentTeams?.map((team, i) => (
              <Flex direction="right" align="center" gap="xs" key={team.id}>
                <TeamLogo
                  src={team.image}
                  nbaTeamId={team.nbaId || team.id}
                  leagueId={team.domesticLeagueId}
                  size="3xs"
                />
                <Text size="xs" key={team.id} className="text-nowrap">
                  {team.fullName}
                  {i < player.currentTeams.length - 1 ? "," : ""}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Text size="3xl" heading>
        {title}
      </Text>
    </Flex>
  );
}

function ScoutingAndAnalyticsPage1({
  player,
  scores = [],
  offenseSummary,
  defenseSummary,
  otherSummary,
}: {
  player: PlayerRow;
  scores?: PlayerMetadataScoreRow[] | null;
  offenseSummary?: PlayerSummaryRow | null;
  defenseSummary?: PlayerSummaryRow | null;
  otherSummary?: PlayerSummaryRow | null;
}) {
  const offenseScores = SkillScoreSections.OFFENSE.map(
    (section) => section.scores
  ).flat();

  const offenseScoresColumn1 = offenseScores.slice(0, 6).map((score) => ({
    key: score,
    value: scores?.find((s) => s.key === score)?.value ?? 0,
    label:
      ReportScoreDescriptions[score].fullLabel ||
      ReportScoreDescriptions[score].label,
  }));

  const offenseScoresColumn2 = offenseScores
    .slice(6, offenseScores.length)
    .map((score) => ({
      key: score,
      value: Math.round(scores?.find((s) => s.key === score)?.value ?? 0),
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  const defenseScores = SkillScoreSections.DEFENSE.map(
    (section) => section.scores
  )
    .flat()
    .map((score) => ({
      key: score,
      value: scores?.find((s) => s.key === score)?.value ?? 0,
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  const otherScores = SkillScoreSections.OTHER.map((section) => section.scores)
    .flat()
    .map((score) => ({
      key: score,
      value: scores?.find((s) => s.key === score)?.value ?? 0,
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  const hasScores = scores && scores.length > 0;
  const isPro = Boolean(player.nbaId);

  return (
    <Paper player={player}>
      <Flex direction="down" gap="none" className="w-full">
        <SmallPlayerHeader
          player={player}
          title={player.nbaId ? "Scouting" : "Scouting & Analytics"}
        />
        <Separator />

        <Flex direction="down" gap="md" className="my-4 w-full">
          <PlayerAttributes playerMetadata={player.metadata[0]!} print />

          <Flex direction="down" gap="sm">
            <AiEditor
              title="Offense"
              defaultValue={offenseSummary?.value ?? ""}
              readonly={true}
              maxLength={1200}
              className="text-2xs"
            />

            {hasScores && (
              <Flex direction="right" gap="md">
                <ScoreBarChart
                  labels={[1, 2, 3, 4, 5]}
                  scores={offenseScoresColumn1 ?? []}
                  print
                />
                <ScoreBarChart
                  labels={[1, 2, 3, 4, 5]}
                  scores={offenseScoresColumn2 ?? []}
                  print
                />
              </Flex>
            )}
          </Flex>

          <Flex direction="down" gap="sm">
            <AiEditor
              title="Defense"
              defaultValue={defenseSummary?.value ?? ""}
              readonly={true}
              maxLength={1200}
              className="text-2xs"
            />

            {hasScores && (
              <Flex
                direction="right"
                gap="md"
                className="max-h-[145px] w-[85%]"
              >
                <ScoreBarChart
                  labels={[1, 2, 3, 4, 5]}
                  scores={defenseScores ?? []}
                  print
                />
                <Flex className="flex-1">
                  <CanDefendSection
                    scores={scores ?? []}
                    hideTitle={true}
                    print
                  />
                </Flex>
              </Flex>
            )}

            {!hasScores && isPro && (
              <Flex direction="down" gap="sm">
                <AiEditor
                  title="Other Notes"
                  defaultValue={otherSummary?.value ?? ""}
                  readonly={true}
                  maxLength={1200}
                  className="text-2xs"
                />
                {hasScores && (
                  <Flex direction="right" gap="md" className="w-1/2">
                    <ScoreBarChart
                      labels={[1, 2, 3, 4, 5]}
                      scores={otherScores ?? []}
                      print
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
}

function ScoutingAndAnalyticsPage2({
  player,
  scores,
  otherSummary,
}: {
  player: PlayerRow;
  scores?: PlayerMetadataScoreRow[] | null;
  otherSummary?: PlayerSummaryRow | null;
}) {
  const otherScores = SkillScoreSections.OTHER.map((section) => section.scores)
    .flat()
    .map((score) => ({
      key: score,
      value: scores?.find((s) => s.key === score)?.value ?? 0,
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  const isPro = Boolean(player.nbaId);

  return (
    <Paper player={player}>
      <Flex direction="down" gap="none" className="w-full">
        <SmallPlayerHeader player={player} title="Scouting & Analytics" />
        <Separator />

        <Flex direction="down" gap="md" className="my-4 w-full">
          <Flex direction="down" gap="sm">
            <AiEditor
              title="Other Notes"
              defaultValue={otherSummary?.value ?? ""}
              readonly={true}
              maxLength={1200}
              className="text-2xs"
            />
            {scores && scores.length > 0 && (
              <Flex direction="right" gap="md" className="w-1/2">
                <ScoreBarChart
                  labels={[1, 2, 3, 4, 5]}
                  scores={otherScores ?? []}
                  print
                />
              </Flex>
            )}
          </Flex>

          {!isPro && <PlayerAnalytics player={player} print />}
        </Flex>
      </Flex>
    </Paper>
  );
}

function HealthAndPerformancePage({ player }: { player: PlayerRow }) {
  return (
    <Paper player={player}>
      <Flex direction="down" gap="none" className="w-full">
        <SmallPlayerHeader player={player} title="Health & Performance" />
        <Separator />

        <Flex direction="down" gap="md" className="my-4 w-full">
          <Flex direction="down" gap="sm">
            <PlayerHealthAndPerformance player={player} print />
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
}

function ProAnalyticsPage({ player }: { player: PlayerRow }) {
  return (
    <Paper player={player}>
      <Flex direction="down" gap="none" className="w-full">
        <SmallPlayerHeader player={player} title="Analytics" />
        <Separator />

        <Flex direction="down" gap="md" className="my-4 w-full">
          <PlayerAnalytics player={player} print />

          <Flex className="pt-4" direction="down" gap="md">
            <Text heading muted>
              Advanced Box Score
            </Text>
            <TempNbaPlayerStats
              nbaId={player.nbaId!}
              measureType="Advanced"
              print
            />
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
}

function StatsPage({ player }: { player: PlayerRow }) {
  return (
    <Paper player={player}>
      <Flex direction="down" gap="none" className="w-full">
        <SmallPlayerHeader player={player} title="Statistics" />
        <Separator />
        <Flex className="pt-4">
          <PlayersStats player={player} print />
        </Flex>
      </Flex>
    </Paper>
  );
}

function IconLegend() {
  return (
    <Flex direction="right" gap="sm" align="center">
      <Flex direction="right" gap="xs" align="center">
        <ApolloScoutIcon size={16} color={theme.colors.teal.DEFAULT} />
        <Text size="2xs">= Scouting</Text>
      </Flex>

      <Flex direction="right" gap="xs" align="center">
        <ApolloAnalyticsIcon size={16} color={theme.colors.magenta.DEFAULT} />
        <Text size="2xs">= Analytics</Text>
      </Flex>
    </Flex>
  );
}

function QRCode({
  className,
  player,
}: {
  player: PlayerRow;
  className?: string;
}) {
  const [qrCode] = useState<QRCodeStyling>(
    new QRCodeStyling({
      width: 80,
      height: 80,
      data: `https://phxapollo.com/players/${player.id}`,
      image: SunsLogo,
      imageOptions: {
        imageSize: 0.5,
        margin: 1,
      },
    })
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      qrCode.append(ref.current);
    }
  }, [qrCode, ref]);

  return <div ref={ref} className={className} />;
}

function getCountryName(code: string) {
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const countryCodesMap = Object.fromEntries(
    countries.map((c) => [c["alpha-3"], c["alpha-2"]])
  );
  const twoLetterCode = countryCodesMap[code] || code;
  try {
    return regionNames.of(twoLetterCode);
  } catch {
    return null;
  }
}
